import type { ProtocolVersion } from "#version";
import { PROTOCOL_VERSION } from "#version";

export type SdkErrorBody = {
	code: string;
	message: string;
};

export type SdkEventMessage<TType extends string = string, TPayload = unknown> = {
	v: ProtocolVersion;
	kind: "event";
	type: TType;
	payload: TPayload;
};

export type SdkRequestMessage<TType extends string = string, TPayload = unknown> = {
	v: ProtocolVersion;
	kind: "request";
	type: TType;
	id: string;
	payload: TPayload;
};

export type SdkResponseOkMessage<TType extends string = string, TPayload = unknown> = {
	v: ProtocolVersion;
	kind: "response";
	type: TType;
	id: string;
	ok: true;
	payload: TPayload;
};

export type SdkResponseErrMessage<TType extends string = string> = {
	v: ProtocolVersion;
	kind: "response";
	type: TType;
	id: string;
	ok: false;
	error: SdkErrorBody;
};

export type SdkResponseMessage<TType extends string = string, TPayload = unknown> = SdkResponseOkMessage<TType, TPayload> | SdkResponseErrMessage<TType>;

export type SdkEnvelope = SdkEventMessage | SdkRequestMessage | SdkResponseMessage;

export type SdkMessageKind = SdkEnvelope["kind"];

export function isSdkEnvelope(value: unknown): value is SdkEnvelope {
	if (value === null || typeof value !== "object") return false;
	const msg = value as Record<string, unknown>;
	if (msg.v !== PROTOCOL_VERSION) return false;
	if (typeof msg.type !== "string" || msg.type.length === 0) return false;

	switch (msg.kind) {
		case "event":
			return "payload" in msg;
		case "request":
			return typeof msg.id === "string" && msg.id.length > 0 && "payload" in msg;
		case "response":
			if (typeof msg.id !== "string" || msg.id.length === 0) return false;
			if (msg.ok === true) return "payload" in msg;
			if (msg.ok === false) {
				const error = msg.error;
				return (
					error !== null && typeof error === "object" && typeof (error as SdkErrorBody).code === "string" && typeof (error as SdkErrorBody).message === "string"
				);
			}
			return false;
		default:
			return false;
	}
}
